import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ServerIcon,
  PhoneIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PhoneArrowUpRightIcon,
  Cog8ToothIcon,
  ArrowPathIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';
import {
  StatCard,
  StatGrid,
  StatusPill,
  SectionCard,
  Spinner,
  CARD,
  BORDER,
  SURFACE_HEADER,
  TEXT_MUTED,
  TEXT_PRIMARY,
  btn,
} from './components';
import { formatCurrency, asArray } from './components/format';

const ACTIVE_POLL_MS = 5000;

/**
 * Voice module landing screen.
 *
 * Rebuilt on the shared kit. Also removes a verbatim duplicate of
 * fetchDashboardData that existed only so the refresh button could set a
 * different loading flag.
 */
const VoiceDashboard = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [activeCalls, setActiveCalls] = useState(0);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const [dashboardRes, healthRes] = await Promise.all([
          axiosInstance.get('/api/sip/dashboard'),
          axiosInstance.get('/api/sip/health'),
        ]);
        setStats(dashboardRes.data.data);
        setHealth(healthRes.data.data);
        setActiveCalls(dashboardRes.data.data?.activeCalls || 0);
      } catch (error) {
        logger.error('Error fetching voice dashboard:', error);
        showNotification?.('Failed to load voice dashboard', 'error');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [showNotification]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Poll only the live-call count; the rest of the dashboard doesn't change
  // fast enough to be worth refetching every 5s.
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axiosInstance.get('/api/sip/active-calls');
        setActiveCalls(asArray(res.data, 'calls').length);
      } catch {
        // Polling failure is not worth interrupting the user for.
      }
    };
    const interval = setInterval(poll, ACTIVE_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const today = stats?.callsToday || {};
  const monthly = stats?.calls || {};

  const sections = [
    {
      title: 'SIP trunks',
      description: 'Carrier connections and credentials',
      icon: ServerIcon,
      path: '/voice/trunks',
      count: stats?.trunks?.totalTrunks,
      tone: 'info',
    },
    {
      title: 'Phone numbers',
      description: 'Buy, assign and route DIDs',
      icon: PhoneIcon,
      path: '/voice/dids',
      count: stats?.dids?.totalDIDs,
      tone: 'success',
    },
    {
      title: 'Active calls',
      description: 'Monitor live calls',
      icon: PhoneArrowUpRightIcon,
      path: '/voice/active',
      count: activeCalls,
      tone: 'danger',
      highlight: activeCalls > 0,
    },
    {
      title: 'Call history',
      description: 'Records, recordings and analytics',
      icon: ClockIcon,
      path: '/voice/history',
      tone: 'accent',
    },
    {
      title: 'Rate cards',
      description: 'Pricing and billing rates',
      icon: CurrencyDollarIcon,
      path: '/voice/ratecards',
      tone: 'warning',
    },
    {
      title: 'Settings',
      description: 'Global voice configuration',
      icon: Cog8ToothIcon,
      path: '/voice/settings',
      tone: 'neutral',
    },
  ];

  const healthy = health?.status === 'healthy';

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-[#0d0d0d]">
      <div className={`sticky top-0 z-20 border-b ${BORDER} ${SURFACE_HEADER} backdrop-blur`}>
        <div className="px-6 py-4 flex items-center gap-4 max-w-7xl mx-auto">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <h1 className={`text-xl font-semibold ${TEXT_PRIMARY}`}>Voice</h1>
              {health && (
                <StatusPill
                  status={healthy ? 'healthy' : 'unhealthy'}
                  label={healthy ? 'Operational' : 'Degraded'}
                />
              )}
            </div>
            <p className={`text-[13px] mt-0.5 ${TEXT_MUTED}`}>
              Telephony, numbers and call records
            </p>
          </div>
          <button
            onClick={() => load({ silent: true })}
            className={btn('secondary', 'sm')}
            disabled={refreshing}
          >
            {refreshing ? <Spinner size="xs" inherit /> : <ArrowPathIcon className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {activeCalls > 0 && (
          <button
            onClick={() => navigate('/voice/active')}
            className={`w-full ${CARD} p-4 flex items-center gap-3 text-left hover:border-red-500/40 transition-colors`}
          >
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${TEXT_PRIMARY}`}>
                {activeCalls} call{activeCalls !== 1 ? 's' : ''} in progress
              </p>
              <p className={`text-[12px] ${TEXT_MUTED}`}>Monitor and manage live calls</p>
            </div>
            <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </button>
        )}

        <div>
          <h2 className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${TEXT_MUTED}`}>
            Today
          </h2>
          {loading ? (
            <SkeletonRow />
          ) : (
            <StatGrid cols={4}>
              <StatCard label="Active trunks" value={stats?.trunks?.activeTrunks || 0} icon={ServerIcon} tone="info" />
              <StatCard label="Active numbers" value={stats?.dids?.activeDIDs || 0} icon={PhoneIcon} tone="success" />
              <StatCard label="Calls today" value={today.totalCalls || 0} icon={PhoneArrowUpRightIcon} tone="accent" />
              <StatCard
                label="Minutes today"
                value={Math.round((today.totalTalkTime || 0) / 60)}
                icon={ClockIcon}
                tone="warning"
              />
            </StatGrid>
          )}
        </div>

        <div>
          <h2 className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${TEXT_MUTED}`}>
            Manage
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sections.map((s) => (
              <SectionTile key={s.path} {...s} onClick={() => navigate(s.path)} />
            ))}
          </div>
        </div>

        {/* Last 30 days. Links through to Call History rather than
            duplicating its numbers in two places. */}
        <SectionCard
          title="Last 30 days"
          actions={
            <button onClick={() => navigate('/voice/history')} className={btn('ghost', 'sm')}>
              View history
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </button>
          }
        >
          {loading ? (
            <SkeletonRow />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <Metric label="Total calls" value={(monthly.totalCalls || 0).toLocaleString()} />
              <Metric
                label="Answer rate"
                value={monthly.answerRate != null ? `${Math.round(monthly.answerRate)}%` : '—'}
              />
              <Metric
                label="Total minutes"
                value={Math.round((monthly.totalTalkTime || 0) / 60).toLocaleString()}
              />
              <Metric label="Total cost" value={formatCurrency(monthly.totalCost || 0)} />
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

const SectionTile = ({ title, description, icon: Icon, count, tone, highlight, onClick }) => (
  <button
    onClick={onClick}
    className={`${CARD} p-4 text-left flex items-start gap-3 transition-colors hover:border-gray-300 dark:hover:border-white/[0.14] ${
      highlight ? 'ring-1 ring-red-500/30' : ''
    }`}
  >
    <div
      className={`p-2 rounded-lg flex-shrink-0 ${
        {
          info: 'bg-blue-500/10 text-blue-500',
          success: 'bg-emerald-500/10 text-emerald-500',
          danger: 'bg-red-500/10 text-red-500',
          accent: 'bg-orange-500/10 text-orange-500',
          warning: 'bg-amber-500/10 text-amber-500',
          neutral: 'bg-gray-500/10 text-gray-400',
        }[tone] || 'bg-gray-500/10 text-gray-400'
      }`}
    >
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <p className={`text-sm font-medium ${TEXT_PRIMARY}`}>{title}</p>
        {count != null && count > 0 && (
          <span className={`text-[12px] tabular-nums ${TEXT_MUTED}`}>{count}</span>
        )}
      </div>
      <p className={`text-[12px] mt-0.5 ${TEXT_MUTED}`}>{description}</p>
    </div>
  </button>
);

const Metric = ({ label, value }) => (
  <div>
    <p className={`text-xl font-semibold tabular-nums ${TEXT_PRIMARY}`}>{value}</p>
    <p className={`text-[12px] mt-1 ${TEXT_MUTED}`}>{label}</p>
  </div>
);

const SkeletonRow = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={`${CARD} p-4`}>
        <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-white/[0.06] animate-pulse" />
        <div className="mt-3 h-6 w-16 rounded bg-gray-200 dark:bg-white/[0.06] animate-pulse" />
        <div className="mt-2 h-3 w-20 rounded bg-gray-200 dark:bg-white/[0.06] animate-pulse" />
      </div>
    ))}
  </div>
);

export default VoiceDashboard;
